import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  setInstitutionalActDeleted,
  type InstitutionalActRecord,
  type ReviewOrganization,
} from '@urbanik/api-client';
import { Button, CanonicalModal, TextAreaField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
export function InstitutionalActLifecycle({
  organization,
  act,
  onClose,
  onSaved,
}: {
  organization: ReviewOrganization;
  act: InstitutionalActRecord;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState(''),
    [saving, setSaving] = useState(false),
    [error, setError] = useState('');
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  const deleted = !act.deletedAt;
  async function save(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const controller = new AbortController();
    pending.current = controller;
    setSaving(true);
    setError('');
    try {
      await setInstitutionalActDeleted(
        reviewApiBase,
        organization.id,
        act.id,
        deleted,
        act.version,
        reason,
        controller.signal,
      );
      if (!controller.signal.aborted) onSaved();
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : 'No se pudo cambiar el estado.');
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  }
  return (
    <CanonicalModal
      open
      title={deleted ? 'Enviar acta a papelera' : 'Restaurar acta'}
      description={organization.name + ' · ' + act.number}
      onClose={() => {
        if (!saving) onClose();
      }}
    >
      <form onSubmit={(event) => void save(event)}>
        <p>
          El cambio es reversible. Se conservan el documento, las reglas vinculadas y el historial.
          No se anulan obligaciones ni pagos.
        </p>
        {error ? <p role="alert">{error}</p> : null}
        <TextAreaField
          label="Motivo del cambio"
          required
          minLength={5}
          maxLength={500}
          disabled={saving}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Button type="button" disabled={saving} onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={saving || reason.trim().length < 5}>
          {saving
            ? 'Guardando…'
            : deleted
              ? 'Confirmar envío a papelera'
              : 'Confirmar restauración'}
        </Button>
      </form>
    </CanonicalModal>
  );
}
